/** Every sound in the game, written down as numbers.
 *
 * There are no audio files. Not for want of trying to find some: a sample pack
 * is a licence question and a megabyte per cue, and the thirty sounds this game
 * needs are thirty short envelopes over an oscillator and a noise burst - which
 * is what a 1990s sound card was doing anyway. The whole kit below is about
 * four kilobytes of table and it loads with the page.
 *
 * A `Patch` is a bag of `Voice`s. A `Voice` is one source (an oscillator or the
 * shared noise buffer), one optional filter, and one attack-hold-decay envelope,
 * starting at an offset from the cue. Layering two or three of them is enough
 * for everything here: a sword is a filtered noise burst over a low square
 * thump, a fanfare is four triangles at staggered delays, a coin is two sines a
 * fourth apart.
 *
 * Amplitudes are deliberately low - nothing peaks above 0.22 - because a cue is
 * allowed to land on top of two others and the master bus has no limiter.
 */

export type Source = OscillatorType | "noise";

export interface Filter {
  readonly type: BiquadFilterType;
  readonly freq: number;
  /** Swept to over the voice's length when given. */
  readonly freqTo?: number;
  readonly q?: number;
}

export interface Voice {
  readonly src: Source;
  /** Ignored for `"noise"`, which has no pitch of its own. */
  readonly freq?: number;
  /** Glided to over the voice's length. */
  readonly freqTo?: number;
  readonly gain: number;
  /** Seconds. `attack + hold + decay` is how long the voice lasts. */
  readonly attack: number;
  readonly hold?: number;
  readonly decay: number;
  /** Seconds after the cue that this voice starts. */
  readonly delay?: number;
  readonly filter?: Filter;
  readonly detune?: number;
}

export interface Patch {
  readonly voices: readonly Voice[];
}

// ------------------------------------------------------------------ notes

/**
 * Equal temperament from A4 = 440. Written as a function rather than a table
 * because the patches below are easier to read as note names than as numbers,
 * and a wrong number in a chord is invisible while a wrong note name is not.
 */
const A4 = 440;
const SEMITONE_OF: Readonly<Record<string, number>> = Object.freeze({
  C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2,
});

export function note(name: string): number {
  const letter = name[0] ?? "A";
  const sharp = name[1] === "#";
  const octave = Number.parseInt(name.slice(sharp ? 2 : 1), 10);
  const semis = (SEMITONE_OF[letter] ?? 0) + (sharp ? 1 : 0) + (octave - 4) * 12;
  return A4 * Math.pow(2, semis / 12);
}

/** One voice per note, staggered - which is what an arpeggio is. */
function arpeggio(
  names: readonly string[],
  over: Omit<Voice, "freq" | "delay">,
  step: number,
  from = 0,
): Voice[] {
  return names.map((n, i) => ({ ...over, freq: note(n), delay: from + i * step }));
}

// ----------------------------------------------------------------- patches

const CLICK: Patch = {
  voices: [{
    src: "square", freq: 880, freqTo: 620, gain: 0.10,
    attack: 0.001, decay: 0.045, filter: { type: "lowpass", freq: 3200 },
  }],
};

const NOISE_HIT = (
  freq: number, q: number, decay: number, gain: number, delay = 0,
): Voice => ({
  src: "noise", gain, attack: 0.002, decay, delay,
  filter: { type: "bandpass", freq, q },
});

export const PATCHES: Readonly<Record<string, Patch>> = Object.freeze({
  // ---- shell. Short, quiet, and pitched above the music bed so they cut
  // through it without needing volume.
  "ui.click": CLICK,
  "ui.close": {
    voices: [{
      src: "square", freq: 620, freqTo: 420, gain: 0.09,
      attack: 0.001, decay: 0.055, filter: { type: "lowpass", freq: 2600 },
    }],
  },
  "ui.refused": {
    voices: [
      { src: "square", freq: 196, gain: 0.11, attack: 0.002, decay: 0.10,
        filter: { type: "lowpass", freq: 1400 } },
      { src: "square", freq: 165, gain: 0.11, attack: 0.002, decay: 0.16, delay: 0.07,
        filter: { type: "lowpass", freq: 1200 } },
    ],
  },
  "prompt.open": {
    voices: [{
      src: "triangle", freq: note("C5"), freqTo: note("G5"), gain: 0.09,
      attack: 0.008, decay: 0.12,
    }],
  },

  // ---- calendar. One note, three notes, four notes: the player learns the
  // length before he learns the tune.
  "turn.start": {
    voices: arpeggio(["G4", "C5"], { src: "triangle", gain: 0.09, attack: 0.005, decay: 0.16 }, 0.07),
  },
  "week.start": {
    voices: arpeggio(["C5", "E5", "G5"], { src: "triangle", gain: 0.10, attack: 0.005, decay: 0.22 }, 0.085),
  },
  "month.start": {
    voices: [
      ...arpeggio(["C5", "E5", "G5", "C6"], { src: "triangle", gain: 0.10, attack: 0.006, decay: 0.30 }, 0.09),
      { src: "sine", freq: note("C3"), gain: 0.08, attack: 0.02, hold: 0.15, decay: 0.5 },
    ],
  },

  // ---- adventure
  "hero.move": { voices: [NOISE_HIT(820, 1.1, 0.045, 0.055)] },
  "hero.levelup": {
    voices: [
      ...arpeggio(["C5", "E5", "G5", "C6"], { src: "triangle", gain: 0.12, attack: 0.005, decay: 0.28 }, 0.075),
      { src: "sine", freq: note("C6"), gain: 0.07, attack: 0.01, hold: 0.1, decay: 0.6, delay: 0.3 },
    ],
  },
  "hero.hire": {
    voices: [
      { src: "triangle", freq: note("E4"), freqTo: note("B4"), gain: 0.11, attack: 0.01, decay: 0.22 },
      NOISE_HIT(1600, 0.9, 0.10, 0.04, 0.02),
    ],
  },
  "hero.defeated": {
    voices: [
      { src: "sawtooth", freq: note("A3"), freqTo: 70, gain: 0.14, attack: 0.01, decay: 0.55,
        filter: { type: "lowpass", freq: 1400, freqTo: 220 } },
      NOISE_HIT(300, 0.6, 0.45, 0.06),
    ],
  },
  "object.pickup": {
    voices: [
      { src: "sine", freq: note("B5"), gain: 0.09, attack: 0.002, decay: 0.09 },
      { src: "sine", freq: note("E6"), gain: 0.08, attack: 0.002, decay: 0.14, delay: 0.055 },
    ],
  },
  "object.capture": {
    voices: [
      ...arpeggio(["A4", "E5"], { src: "triangle", gain: 0.10, attack: 0.006, decay: 0.24 }, 0.09),
      NOISE_HIT(2400, 0.7, 0.22, 0.035, 0.05),
    ],
  },
  // A coin is two bright sines a fourth apart with almost no attack; the
  // high-pass is what stops it reading as a bell.
  "resource.gain": {
    voices: [
      { src: "sine", freq: 1240, gain: 0.075, attack: 0.001, decay: 0.06,
        filter: { type: "highpass", freq: 700 } },
      { src: "sine", freq: 1660, gain: 0.065, attack: 0.001, decay: 0.10, delay: 0.035,
        filter: { type: "highpass", freq: 700 } },
    ],
  },
  "resource.spend": {
    voices: [{ src: "sine", freq: 900, freqTo: 520, gain: 0.07, attack: 0.002, decay: 0.11 }],
  },
  "artifact.gain": {
    voices: [
      { src: "sine", freq: note("C6"), gain: 0.07, attack: 0.01, decay: 0.55 },
      { src: "sine", freq: note("E6"), gain: 0.06, attack: 0.01, decay: 0.55, delay: 0.08 },
      { src: "sine", freq: note("G6"), gain: 0.05, attack: 0.01, decay: 0.65, delay: 0.16 },
    ],
  },
  "spell.learn": {
    voices: [
      { src: "sine", freq: 420, freqTo: 1700, gain: 0.09, attack: 0.02, decay: 0.32,
        filter: { type: "bandpass", freq: 1200, q: 2 } },
      NOISE_HIT(3200, 1.4, 0.30, 0.03, 0.05),
    ],
  },

  // ---- town
  "town.build": {
    voices: [
      NOISE_HIT(700, 0.8, 0.09, 0.09),
      { src: "square", freq: 150, gain: 0.10, attack: 0.002, decay: 0.14,
        filter: { type: "lowpass", freq: 700 } },
      NOISE_HIT(700, 0.8, 0.12, 0.08, 0.16),
      { src: "square", freq: 130, gain: 0.09, attack: 0.002, decay: 0.20, delay: 0.16,
        filter: { type: "lowpass", freq: 600 } },
    ],
  },
  // A horn, not a fanfare: a town changing hands is news, and news is two
  // sawtooth notes under a low-pass, not a major triad.
  "town.capture": {
    voices: [
      { src: "sawtooth", freq: note("A3"), gain: 0.13, attack: 0.03, hold: 0.10, decay: 0.30,
        filter: { type: "lowpass", freq: 1100 } },
      { src: "sawtooth", freq: note("E4"), gain: 0.13, attack: 0.03, hold: 0.14, decay: 0.45,
        delay: 0.24, filter: { type: "lowpass", freq: 1300 } },
    ],
  },
  "town.recruit": {
    voices: [
      ...arpeggio(["C5", "E5"], { src: "triangle", gain: 0.09, attack: 0.004, decay: 0.15 }, 0.06),
      NOISE_HIT(1200, 1.0, 0.08, 0.04),
    ],
  },

  // ---- battle
  "battle.start": {
    voices: [
      { src: "sawtooth", freq: note("A2"), freqTo: note("A3"), gain: 0.15, attack: 0.05,
        hold: 0.12, decay: 0.5, filter: { type: "lowpass", freq: 700, freqTo: 1800 } },
      NOISE_HIT(240, 0.5, 0.6, 0.06),
    ],
  },
  "battle.melee": {
    voices: [
      NOISE_HIT(2100, 1.6, 0.07, 0.10),
      { src: "square", freq: 120, gain: 0.09, attack: 0.001, decay: 0.09,
        filter: { type: "lowpass", freq: 500 } },
    ],
  },
  // An arrow is the sweep, not the twang: a rising high-pass over noise is what
  // "past your ear" sounds like.
  "battle.ranged": {
    voices: [{
      src: "noise", gain: 0.08, attack: 0.005, decay: 0.13,
      filter: { type: "highpass", freq: 1800, freqTo: 6000 },
    }],
  },
  "battle.death": {
    voices: [
      { src: "sawtooth", freq: 165, freqTo: 58, gain: 0.11, attack: 0.005, decay: 0.38,
        filter: { type: "lowpass", freq: 1100, freqTo: 200 } },
      NOISE_HIT(420, 0.5, 0.34, 0.06),
    ],
  },
  "battle.spell": {
    voices: [
      { src: "sine", freq: 1900, freqTo: 320, gain: 0.09, attack: 0.008, decay: 0.34,
        filter: { type: "bandpass", freq: 1400, q: 1.5 } },
      NOISE_HIT(2800, 1.2, 0.28, 0.04, 0.03),
    ],
  },
  "battle.wall": {
    voices: [
      { src: "square", freq: 70, gain: 0.14, attack: 0.004, decay: 0.5,
        filter: { type: "lowpass", freq: 320 } },
      NOISE_HIT(180, 0.4, 0.55, 0.09),
    ],
  },
  "battle.morale": {
    voices: arpeggio(["E5", "A5"], { src: "triangle", gain: 0.09, attack: 0.004, decay: 0.16 }, 0.06),
  },
  "battle.luck": {
    voices: arpeggio(["E6", "A6", "C7"], { src: "sine", gain: 0.06, attack: 0.002, decay: 0.13 }, 0.045),
  },

  // ---- the end. Long enough to be an ending rather than a cue.
  "game.win": {
    voices: [
      ...arpeggio(["C5", "E5", "G5", "C6"],
        { src: "triangle", gain: 0.13, attack: 0.01, hold: 0.06, decay: 0.5 }, 0.14),
      ...arpeggio(["C4", "E4", "G4", "C5"],
        { src: "sawtooth", gain: 0.07, attack: 0.02, hold: 0.06, decay: 0.55,
          filter: { type: "lowpass", freq: 1600 } }, 0.14),
      { src: "sine", freq: note("C3"), gain: 0.09, attack: 0.05, hold: 0.5, decay: 0.9, delay: 0.4 },
    ],
  },
  "game.lose": {
    voices: [
      ...arpeggio(["E4", "C4", "A3"],
        { src: "sawtooth", gain: 0.10, attack: 0.03, hold: 0.15, decay: 0.6,
          filter: { type: "lowpass", freq: 900 } }, 0.30),
      { src: "sine", freq: note("A2"), gain: 0.09, attack: 0.08, hold: 0.6, decay: 1.1, delay: 0.3 },
    ],
  },
});

// ------------------------------------------------------------------ graph

/** How long a voice occupies the graph, including its start offset. */
export const voiceLength = (v: Voice): number =>
  (v.delay ?? 0) + v.attack + (v.hold ?? 0) + v.decay;

export const patchLength = (p: Patch): number =>
  p.voices.reduce((m, v) => Math.max(m, voiceLength(v)), 0);

/** Two seconds of white noise, made once and shared by every noise voice. */
export function makeNoise(ctx: BaseAudioContext, seconds = 2): AudioBuffer {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const data = buf.getChannelData(0);
  // A fixed sequence rather than `Math.random`, so two runs of the game make
  // the same noise and a recorded still is reproducible. This is a linear
  // congruential generator; nothing here needs a better one.
  let seed = 0x2f6e2b1;
  for (let i = 0; i < data.length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    data[i] = (seed / 0x80000000) - 1;
  }
  return buf;
}

/**
 * Schedule one patch. Returns when the last voice falls silent.
 *
 * Every node is created here and dropped on the floor: Web Audio keeps a
 * started source alive until it stops and then collects it, so there is nothing
 * to release and nothing to pool. The one thing that must not be skipped is
 * `stop()` - a source without one runs until the context is closed.
 */
export function playPatch(
  ctx: BaseAudioContext, dest: AudioNode, patch: Patch, noise: AudioBuffer, when: number,
): number {
  let end = when;
  for (const v of patch.voices) {
    const t0 = when + (v.delay ?? 0);
    const len = v.attack + (v.hold ?? 0) + v.decay;
    const t1 = t0 + len;
    end = Math.max(end, t1);

    let src: AudioScheduledSourceNode;
    if (v.src === "noise") {
      const n = ctx.createBufferSource();
      n.buffer = noise;
      n.loop = true;
      src = n;
    } else {
      const o = ctx.createOscillator();
      o.type = v.src;
      o.frequency.setValueAtTime(v.freq ?? 440, t0);
      if (v.freqTo !== undefined) {
        o.frequency.exponentialRampToValueAtTime(Math.max(1, v.freqTo), t1);
      }
      if (v.detune !== undefined) o.detune.setValueAtTime(v.detune, t0);
      src = o;
    }

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(v.gain, t0 + Math.max(0.001, v.attack));
    const holdEnd = t0 + v.attack + (v.hold ?? 0);
    env.gain.setValueAtTime(v.gain, holdEnd);
    // Exponential, not linear: a linear tail on a short percussive envelope
    // reads as a click at the moment it hits zero.
    env.gain.exponentialRampToValueAtTime(0.0001, t1);

    let head: AudioNode = env;
    if (v.filter !== undefined) {
      const f = ctx.createBiquadFilter();
      f.type = v.filter.type;
      f.frequency.setValueAtTime(v.filter.freq, t0);
      if (v.filter.freqTo !== undefined) {
        f.frequency.exponentialRampToValueAtTime(Math.max(1, v.filter.freqTo), t1);
      }
      if (v.filter.q !== undefined) f.Q.setValueAtTime(v.filter.q, t0);
      f.connect(env);
      head = f;
    }
    env.connect(dest);
    src.connect(head);
    src.start(t0);
    src.stop(t1 + 0.02);
  }
  return end;
}
